/**
 * ChatbotWidget — floating popup chat window.
 * Wires the input to the disaster-data chatbot backend endpoint.
 * Anchored bottom-right; opens just above the toggle button.
 *
 * antd 4.x APIs only (no FloatButton, no Drawer).
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, Tag, Input, message as antdMessage } from 'antd';
import { MessageOutlined, CloseOutlined, SendOutlined, QuestionCircleOutlined, PaperClipOutlined, FilePdfOutlined, CopyOutlined } from '@ant-design/icons';
import Markdown from 'markdown-to-jsx';
import * as XLSX from 'xlsx';
import { LOGIN_USER } from 'redux/constants/Auth';
import { APP_PREFIX_PATH } from 'configs/AppConfig';
import { USER_ROLES } from 'constants/ApplicationConstant';
import getRoleLabel from './getRoleLabel';
import ChatbotService from 'services/ChatbotService';
import { getRandomQuestions } from './chatbotSampleQuestions';
import './ChatbotWidget.css';

// ---------------------------------------------------------------------------
// Attachment limits — UX-only mirrors of the authoritative backend caps.
// The backend re-validates everything; these are for immediate user feedback only.
// ---------------------------------------------------------------------------
const MAX_ATTACHMENTS = 2;           // backend: CHATBOT_MAX_ATTACHMENTS (default 2)
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;  // backend: CHATBOT_MAX_IMAGE_BYTES (default 2 MB)
const MAX_PDF_BYTES = 2 * 1024 * 1024;    // backend: CHATBOT_MAX_PDF_BYTES (default 2 MB)

/** MIME types accepted for images */
const ACCEPTED_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

/** Default message when user attaches file(s) but leaves text empty */
const DEFAULT_ATTACHMENT_MESSAGE = 'Please analyse the attached file(s).';

/**
 * MarkdownTable — plain function component (NOT forwardRef) that wraps a
 * markdown-to-jsx <table> with Export (Excel) + Copy toolbar buttons.
 * markdown-to-jsx overrides only work reliably with plain function components.
 */
function MarkdownTable({ children }) {
  const tableRef = useRef(null);

  const handleExcel = () => {
    const table = tableRef.current;
    if (!table) return;
    const rows = Array.from(table.rows).map((row) =>
      Array.from(row.cells).map((cell) => cell.innerText)
    );
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, 'drims-table.xlsx');
  };

  const handleCopy = () => {
    const table = tableRef.current;
    if (!table) return;
    const tsv = Array.from(table.rows)
      .map((row) =>
        Array.from(row.cells)
          .map((cell) => cell.innerText)
          .join('\t')
      )
      .join('\n');
    navigator.clipboard.writeText(tsv).then(() => {
      antdMessage.success('Table copied');
    });
  };

  return (
    <div className="chatbot-md-tableblock">
      <div className="chatbot-md-toolbar">
        <button
          className="chatbot-md-toolbar__btn"
          onClick={handleExcel}
          title="Download as Excel"
          aria-label="Download table as Excel"
          type="button"
        >
          Excel
        </button>
        <button
          className="chatbot-md-toolbar__btn"
          onClick={handleCopy}
          title="Copy table"
          aria-label="Copy table"
          type="button"
        >
          Copy
        </button>
      </div>
      <div className="chatbot-md-tablewrap">
        <table ref={tableRef} className="chatbot-md-table">{children}</table>
      </div>
    </div>
  );
}

/** markdown-to-jsx options for assistant bubbles — XSS-safe, table scrollable */
const MARKDOWN_OPTIONS = {
  overrides: {
    table: { component: MarkdownTable },
  },
  disableParsingRawHTML: true,
};

/**
 * Resolve the logged-in user — prefer Redux state, fallback to localStorage.
 * @param {object|null} loginUserFromRedux
 * @returns {object|null}
 */
function resolveUser(loginUserFromRedux) {
  if (loginUserFromRedux) return loginUserFromRedux;
  try {
    const raw = localStorage.getItem(LOGIN_USER);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

/**
 * Build the greeting message text for the initial bot bubble.
 */
function buildGreeting(loginUser) {
  if (!loginUser) {
    return "You don't appear to be logged in.";
  }
  const roleId = loginUser.role?.id;
  const roleLabel = getRoleLabel(roleId);
  const username = loginUser.username || loginUser.email || 'there';
  const districtName = loginUser.district?.districtName || '—';
  const blockName = loginUser.block?.blockName || '—';

  return (
    `Hi ${username}! You're signed in as ${roleLabel}. ` +
    `District: ${districtName} / Revenue Circle: ${blockName}. ` +
    `Ask me about any disaster — deaths, relief camps, affected population, damage, and more.`
  );
}

/**
 * Build the LLM conversation history from the widget's message list.
 * Excludes greeting bubbles (UI-only) and error bubbles (failed/degenerate
 * responses, not real assistant turns), mapping to the {role, content} shape
 * the backend expects.
 *
 * For turns that had attachments, a short text placeholder is appended to the
 * content so history stays text-only ({role, content: string}) and files are
 * NEVER re-sent in subsequent turns. The backend's normalizeHistory already
 * requires typeof content === "string" and auto-drops anything else.
 *
 * @param {Array<{from:string, text:string, greeting?:boolean, error?:boolean, attachments?:Array}>} messages
 * @returns {Array<{role:string, content:string}>}
 */
export function buildHistory(messages) {
  return messages
    .filter((m) => !m.greeting && !m.error)
    .map((m) => {
      let content = m.text;
      // Append attachment placeholder so history stays text-only
      if (m.attachments && m.attachments.length > 0) {
        const names = m.attachments.map((a) => a.name).join(', ');
        content = content + '\n[attached: ' + names + ']';
      }
      return {
        role: m.from === 'user' ? 'user' : 'assistant',
        content,
      };
    });
}

/**
 * Validate a File the user is trying to attach.
 * Returns a human-readable error string, or null if the file is acceptable.
 * NOTE: This is UX-only — the backend is authoritative and re-validates.
 * @param {File} file
 * @param {number} currentCount  Number of attachments already selected
 * @returns {string|null}
 */
export function validateAttachment(file, currentCount) {
  if (currentCount >= MAX_ATTACHMENTS) {
    return `You can only attach up to ${MAX_ATTACHMENTS} files.`;
  }
  const isImage = ACCEPTED_IMAGE_TYPES.has(file.type);
  const isPdf = file.type === 'application/pdf';
  if (!isImage && !isPdf) {
    return `"${file.name}" is not a supported type. Attach JPEG, PNG, GIF, WebP images or PDF files.`;
  }
  if (isImage && file.size > MAX_IMAGE_BYTES) {
    return `"${file.name}" exceeds the 2 MB image size limit.`;
  }
  if (isPdf && file.size > MAX_PDF_BYTES) {
    return `"${file.name}" exceeds the 2 MB PDF size limit.`;
  }
  return null;
}

const ChatbotWidget = ({ loginUser: loginUserFromRedux }) => {
  const loginUser = resolveUser(loginUserFromRedux);
  const history = useHistory();

  // All hooks must be called unconditionally (Rules of Hooks)
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => [
    { from: 'bot', text: buildGreeting(loginUser), greeting: true },
  ]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestionChips, setSuggestionChips] = useState([]);
  // attachments: Array of { file, kind:'image'|'pdf', name, size, previewUrl }
  // previewUrl is an object URL created via URL.createObjectURL for images only.
  const [attachments, setAttachments] = useState([]);
  const attachInputRef = useRef(null);
  // Tracks EVERY object URL we create (pending + already-sent) so we can revoke
  // them all on unmount. We deliberately do NOT revoke a URL on send — the sent
  // user bubble still renders the image thumbnail from that URL, so it must stay
  // alive for the session. Revoking happens only on remove (pre-send) or unmount.
  const objectUrlsRef = useRef([]);
  const bodyRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages]);

  // Re-randomize suggestion chips each time the widget opens
  useEffect(() => {
    if (open) {
      setSuggestionChips(getRandomQuestions(5));
    }
  }, [open]);

  // Keep the greeting bubble in sync if the logged-in user hydrates after mount.
  // Redux/localStorage can populate just after first render, which would
  // otherwise leave the greeting stuck on the "not logged in" fallback text.
  useEffect(() => {
    setMessages((prev) => {
      if (!prev.length || !prev[0].greeting) return prev;
      const text = buildGreeting(loginUser);
      if (prev[0].text === text) return prev;
      const next = prev.slice();
      next[0] = { ...next[0], text };
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginUser?.username, loginUser?.email, loginUser?.role?.id]);

  // Revoke ALL created object URLs on unmount to avoid memory leaks.
  // Reads from objectUrlsRef (not `attachments`) so it captures every URL
  // created during the session — including those still shown in sent bubbles —
  // rather than a stale snapshot of `attachments` from mount time.
  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlsRef.current = [];
    };
  }, []);

  // Show widget for all operational roles (3–14, every entry in USER_ROLES); hide for unauthenticated and non-operational roles
  const roleId = loginUser?.role?.id;
  const isOperationalRole = roleId != null && USER_ROLES.some((r) => r.value === roleId);
  if (!loginUser || !isOperationalRole) return null;

  const toggle = () => setOpen((prev) => !prev);
  const close = () => setOpen(false);

  /**
   * Handle file selection from the hidden file input.
   * Validates each new file (count, type, size) and adds valid ones to state.
   */
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    // Reset the input so the same file can be re-selected after a remove
    if (attachInputRef.current) attachInputRef.current.value = '';

    let nextAttachments = [...attachments];
    files.forEach((file) => {
      const error = validateAttachment(file, nextAttachments.length);
      if (error) {
        antdMessage.error(error);
        return;
      }
      const isImage = ACCEPTED_IMAGE_TYPES.has(file.type);
      const previewUrl = isImage ? URL.createObjectURL(file) : null;
      if (previewUrl) objectUrlsRef.current.push(previewUrl);
      nextAttachments.push({
        file,
        kind: isImage ? 'image' : 'pdf',
        name: file.name,
        size: file.size,
        previewUrl,
      });
    });
    setAttachments(nextAttachments);
  };

  /**
   * Remove an attachment chip by index, revoking its object URL if present.
   */
  const handleRemoveAttachment = (idx) => {
    setAttachments((prev) => {
      const next = [...prev];
      const removed = next.splice(idx, 1);
      // Removed before sending → safe to revoke now (not shown in any bubble).
      removed.forEach((a) => {
        if (a.previewUrl) {
          URL.revokeObjectURL(a.previewUrl);
          objectUrlsRef.current = objectUrlsRef.current.filter(
            (url) => url !== a.previewUrl
          );
        }
      });
      return next;
    });
  };

  /**
   * Clicking a suggestion chip immediately submits that question.
   * We hide the chips after the first submission so they don't linger mid-conversation.
   */
  const handleChipClick = async (question) => {
    if (loading) return;
    // History excludes greeting + error bubbles (see buildHistory helper).
    // Built from current messages BEFORE pushing the new user turn.
    const history = buildHistory(messages);
    // Hide chips after a chip is used
    setSuggestionChips([]);
    // Push user message immediately for snappy UX
    setMessages((prev) => [...prev, { from: 'user', text: question }]);
    setLoading(true);
    try {
      const response = await ChatbotService.ask(question, history);
      const hasAnswer = response && typeof response.answer === 'string';
      const answer = hasAnswer
        ? response.answer
        : "Sorry, I couldn't read that response.";
      setMessages((prev) => [
        ...prev,
        { from: 'bot', text: answer, error: !hasAnswer },
      ]);
    } catch (_err) {
      setMessages((prev) => [
        ...prev,
        { from: 'bot', text: 'Sorry, something went wrong.', error: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    const trimmed = inputText.trim();
    const hasAttachments = attachments.length > 0;

    // Allow send when EITHER text OR attachments are present
    if (!trimmed && !hasAttachments) return;
    if (loading) return;

    // If attachments exist but no text, use a sensible default
    const text = trimmed || DEFAULT_ATTACHMENT_MESSAGE;

    // History excludes greeting + error bubbles (see buildHistory helper).
    // Built from current messages BEFORE pushing the new user turn.
    const history = buildHistory(messages);

    // Snapshot the current attachments for this send (used in the user bubble)
    const sendingAttachments = [...attachments];
    // Extract raw File objects for the service call
    const files = sendingAttachments.map((a) => a.file);

    // Push user message (with attachment metadata for rendering)
    setMessages((prev) => [
      ...prev,
      { from: 'user', text, attachments: sendingAttachments },
    ]);
    setInputText('');

    // Clear the pending attachments tray. We do NOT revoke their object URLs
    // here — the sent user bubble still renders those image thumbnails, so the
    // URLs must stay alive. They're all revoked together on unmount (objectUrlsRef).
    setAttachments([]);

    setLoading(true);

    try {
      const response = await ChatbotService.ask(text, history, files);
      // FetchInterceptor returns response.data directly (the parsed body object)
      const hasAnswer = response && typeof response.answer === 'string';
      const answer = hasAnswer
        ? response.answer
        : "Sorry, I couldn't read that response.";
      setMessages((prev) => [
        ...prev,
        { from: 'bot', text: answer, error: !hasAnswer },
      ]);
    } catch (err) {
      // Surface backend's friendly 400 message if available, else generic fallback
      const backendMsg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message;
      const displayMsg = backendMsg
        ? `Error: ${backendMsg}`
        : 'Sorry, something went wrong.';
      setMessages((prev) => [
        ...prev,
        { from: 'bot', text: displayMsg, error: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Send is enabled when text OR attachments are present (and not loading)
  const canSend = (inputText.trim().length > 0 || attachments.length > 0) && !loading;

  // Render into document.body via a portal so `position: fixed` is anchored to
  // the viewport — NOT to any ancestor with a transform/filter/perspective
  // (which would otherwise become the containing block and mis-place the widget).
  return createPortal(
    <>
      {/* Floating toggle button — bottom-right */}
      <Button
        className="chatbot-float-btn"
        type="primary"
        shape="circle"
        icon={open ? <CloseOutlined /> : <MessageOutlined />}
        onClick={toggle}
        title="DRIMS Disaster Assistant"
      />

      {/* Popup chat window */}
      <div
        className={`chatbot-popup${open ? ' chatbot-popup--open' : ''}`}
        role="dialog"
        aria-label="DRIMS Disaster Assistant chat window"
        aria-hidden={!open}
      >
        {/* Header */}
        <div className="chatbot-popup__header">
          <div className="chatbot-popup__header-left">
            <div className="chatbot-popup__avatar" aria-hidden="true">
              <MessageOutlined />
            </div>
            <div className="chatbot-popup__header-text">
              <span className="chatbot-popup__title">DRIMS Assistant</span>
              <span className="chatbot-popup__subtitle">Unified Intelligence Node</span>
            </div>
          </div>
          <div className="chatbot-popup__header-right">
            <span className="chatbot-popup__disaster-pill">ALL DISASTERS</span>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={close}
              aria-label="Close chat window"
              title="Close"
              className="chatbot-popup__close-btn"
            />
          </div>
        </div>

        {/* Body — scrollable message list */}
        <div className="chatbot-popup__body" ref={bodyRef}>
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`chatbot-bubble ${
                msg.from === 'user'
                  ? 'chatbot-bubble--user'
                  : 'chatbot-bubble--assistant chatbot-bubble--markdown'
              }`}
              style={msg.from === 'user' ? { whiteSpace: 'pre-wrap' } : undefined}
            >
              {msg.from === 'user' ? (
                <>
                  {msg.text}
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="chatbot-attachment-chips chatbot-attachment-chips--bubble">
                      {msg.attachments.map((a, aIdx) => (
                        <div key={aIdx} className="chatbot-attachment-chip chatbot-attachment-chip--sent">
                          {a.kind === 'image' && a.previewUrl ? (
                            <img
                              src={a.previewUrl}
                              alt={a.name}
                              className="chatbot-attachment-chip__thumb"
                            />
                          ) : (
                            <FilePdfOutlined className="chatbot-attachment-chip__icon" />
                          )}
                          <span className="chatbot-attachment-chip__name">{a.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <Markdown options={MARKDOWN_OPTIONS}>{msg.text}</Markdown>
                  {/* Per-answer Copy button — only on real assistant answers, not greeting/error */}
                  {!msg.greeting && !msg.error && (
                    <div className="chatbot-bubble__copy-row">
                      <button
                        className="chatbot-bubble__copy-btn"
                        title="Copy answer"
                        aria-label="Copy answer text"
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(msg.text).then(() => {
                            antdMessage.success('Answer copied');
                          });
                        }}
                      >
                        <CopyOutlined style={{ marginRight: 3 }} />
                        Copy
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
          {loading && (
            <div className="chatbot-bubble chatbot-bubble--assistant" aria-label="Assistant is typing">
              <span className="chatbot-typing">
                <span className="chatbot-typing__dot" />
                <span className="chatbot-typing__dot" />
                <span className="chatbot-typing__dot" />
              </span>
            </div>
          )}

          {/* Suggestion chips — shown when the widget first opens, hidden after a chip is used */}
          {suggestionChips.length > 0 && !loading && (
            <div className="chatbot-suggestions" aria-label="Sample questions">
              <p className="chatbot-suggestions__label">Try asking:</p>
              <div className="chatbot-suggestions__chips">
                {suggestionChips.map((q) => (
                  <button
                    key={q}
                    className="chatbot-suggestions__chip"
                    onClick={() => handleChipClick(q)}
                    type="button"
                  >
                    {q}
                  </button>
                ))}
              </div>
              <button
                className="chatbot-suggestions__view-all"
                onClick={() => {
                  close();
                  history.push(`${APP_PREFIX_PATH}/disasters/flood/chatbot-help`);
                }}
                type="button"
              >
                <QuestionCircleOutlined style={{ marginRight: 4 }} />
                View all examples
              </button>
            </div>
          )}
        </div>

        {/* Footer — attachment chips + input row */}
        <div className="chatbot-popup__footer">
          {/* Pending attachment chips — shown above the input when files are selected */}
          {attachments.length > 0 && (
            <div className="chatbot-attachment-chips" aria-label="Selected attachments">
              {attachments.map((a, idx) => (
                <div key={idx} className="chatbot-attachment-chip">
                  {a.kind === 'image' && a.previewUrl ? (
                    <img
                      src={a.previewUrl}
                      alt={a.name}
                      className="chatbot-attachment-chip__thumb"
                    />
                  ) : (
                    <FilePdfOutlined className="chatbot-attachment-chip__icon" />
                  )}
                  <span className="chatbot-attachment-chip__name" title={a.name}>
                    {a.name}
                  </span>
                  <button
                    className="chatbot-attachment-chip__remove"
                    onClick={() => handleRemoveAttachment(idx)}
                    aria-label={`Remove ${a.name}`}
                    title={`Remove ${a.name}`}
                    type="button"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="chatbot-input-row">
            {/* Hidden file input — triggered by the paperclip button */}
            <input
              ref={attachInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
              aria-hidden="true"
              tabIndex={-1}
            />
            <Button
              icon={<PaperClipOutlined />}
              onClick={() => attachInputRef.current && attachInputRef.current.click()}
              disabled={loading || attachments.length >= MAX_ATTACHMENTS}
              aria-label="Attach file"
              title={
                attachments.length >= MAX_ATTACHMENTS
                  ? `Maximum ${MAX_ATTACHMENTS} attachments`
                  : 'Attach image or PDF'
              }
              className="chatbot-attach-btn"
            />
            <Input.TextArea
              placeholder="Ask about any disaster…"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={loading}
              autoSize={{ minRows: 1, maxRows: 4 }}
              className="chatbot-input-row__input"
              aria-label="Type your question"
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              disabled={!canSend}
              onClick={handleSend}
              aria-label="Send message"
              title="Send"
              className="chatbot-send-btn"
            >
              Send
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

const mapStateToProps = ({ auth }) => ({
  loginUser: auth.loginUser,
});

export default connect(mapStateToProps)(ChatbotWidget);
